import React, { useState, useEffect } from 'react';

interface RecipeItem {
  autotreat_mfr_recipe_item_cloud_id: string | number;
  global_liquid_id: string;
  liquid_rate_type_id: string;
  liquid_name: string;
  liquid_rate: string | number;
  liquid_rate_type: string;
  locked: boolean;
  active: boolean;
}

interface RecipeResponse {
  recipe_items?: RecipeItem[];
  [key: string]: any; // Allow other properties
}
import { 
  TextField, 
  Grid, 
  Typography, 
  Checkbox, 
  FormControlLabel, 
  Select, 
  MenuItem, 
  IconButton, 
  Button, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  TableSortLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';

interface Crop {
  crop_id?: number | string;
  id?: number | string;
  name?: string;
  crop_name?: string;
}

interface FormValues {
  [key: string]: any;
  crop?: Crop | null;
  crop_id?: string | number;
  // Add other form fields as needed
}

const Placeholder = styled('em')(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontStyle: 'italic',
}));
import DeleteIcon from '@mui/icons-material/Delete';
import axios from '../../utils/axios.utils';
import { useMfrRecipesNewContext } from '../contexts/MfrRecipesNewContext';
import { useMfrRecipesContext } from '../contexts/MfrRecipesContext';

interface EditMfrRecipeFormProps {
  id: string;
  onSuccess: () => void;
  initialData?: any;
  showSnackbar?: (message: string, severity: 'success' | 'error') => void;
}

const EditMfrRecipeForm: React.FC<EditMfrRecipeFormProps> = ({ id, onSuccess, initialData, showSnackbar = () => {} }) => {
  const [formValues, setFormValues] = useState<FormValues>({
    global_mfr_id: '',
    liquid_recipe_name: '',
    liquid_recipe_desc: '',
    crop_id: '',
    crop: null,
    recipe_volume_max: '',
    recipe_volume_recommended: '',
    seed_cnt_per_scale_uow: '',
    unit_size_cnt: '',
    locked: false,
    active: true,
  });

  const [newRecipeItem, setNewRecipeItem] = useState({
    mfr_liquid: '',
    liquid_name: '',
    liquid_rate: '',
    liquid_rate_type_id: '',
  });

  const { globalMfrs, crops, fetchCrops, fetchManufacturers } = useMfrRecipesNewContext();
  
  // Set up initial form values when component mounts or initialData changes
  useEffect(() => {
    if (initialData) {
      // If we have a crop object in initialData, use it directly
      if (initialData.crop) {
        setFormValues(prev => ({
          ...prev,
          ...initialData,
          crop: initialData.crop,
          crop_id: initialData.crop.crop_id || ''
        }));
      } 
      // Fallback to crop_id if crop object is not available
      else if (initialData.crop_id) {
        const selectedCrop = crops?.find(c => 
          String(c.crop_id) === String(initialData.crop_id) || 
          String(c.id) === String(initialData.crop_id)
        );
        
        if (selectedCrop) {
          setFormValues(prev => ({
            ...prev,
            ...initialData,
            crop: selectedCrop,
            crop_id: selectedCrop.crop_id || selectedCrop.id || ''
          }));
        }
      }
    }
  }, [initialData, crops]);
  const { fetchRecipeItems: contextFetchRecipeItems } = useMfrRecipesContext();
  const [recipeItems, setRecipeItems] = useState<any[]>([]);
  const [mfrLiquids, setMfrLiquids] = useState<any[]>([]);
  const [liquidRateTypes, setLiquidRateTypes] = useState<any[]>([]);
  const [errorMessages, setErrorMessages] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Memoize the fetchRecipeItems function to prevent unnecessary re-renders
  const fetchRecipeItems = React.useCallback(async (recipeId: string): Promise<RecipeItem[]> => {
    if (!recipeId) return [];
    try {
      const response = await axios.get<RecipeResponse>(`/api/admin/autotreat_mfr/mfr_recipes/${recipeId}/`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      });
      
      const recipeData = response.data || {};
      
      // Check for recipe_items in the response
      if (recipeData.recipe_items && Array.isArray(recipeData.recipe_items)) {
        return recipeData.recipe_items;
      }
      
      // Check for items directly in the response
      if (Array.isArray(recipeData)) {
        return recipeData as RecipeItem[];
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching recipe items:', error);
      return [];
    }
  }, []);

  // Effect to fetch initial data once when component mounts
  useEffect(() => {
    let isMounted = true;
    
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch all necessary data first
        await Promise.all([
          fetchManufacturers(),
          fetchCrops(),
          fetchMfrLiquids(),
          fetchLiquidRateTypes(),
        ]);
        
        setInitialLoadComplete(true);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    fetchInitialData();
    
    return () => {
      isMounted = false;
    };
  }, []);
  
  // Effect to update form values when initialData or crops change
  useEffect(() => {
    if (!initialLoadComplete || !initialData) return;
    
    console.log('Initial data received:', initialData);
    console.log('Available crops:', crops);
    
    // If initialData has a crop object, use that directly
    if (initialData.crop) {
      const cropData = initialData.crop as Crop;
      console.log('Using crop from initialData:', cropData);
      
      const newFormValues: FormValues = {
        ...initialData,
        crop: cropData,
        crop_id: cropData.crop_id || cropData.id || ''
      };
      
      console.log('Setting form values with crop from initialData:', newFormValues);
      setFormValues(prev => ({
        ...prev,
        ...newFormValues
      }));
    } 
    // Otherwise, try to find the crop in the fetched crops list
    else if (initialData.crop_id) {
      console.log('Looking for crop with ID:', initialData.crop_id);
      
      // Find the full crop object from the fetched crops
      const selectedCrop = crops?.find(crop => 
        String(crop.crop_id) === String(initialData.crop_id) || 
        String(crop.id) === String(initialData.crop_id)
      ) as Crop | undefined;
      
      console.log('Selected crop found in crops list:', selectedCrop);
      
      if (selectedCrop) {
        const newFormValues: FormValues = {
          ...initialData,
          crop: selectedCrop,
          crop_id: selectedCrop.crop_id || selectedCrop.id || ''
        };
        
        console.log('Setting form values with crop from list:', newFormValues);
        setFormValues(prev => ({
          ...prev,
          ...newFormValues
        }));
      }
    }
    
    // Set initial recipe items if available
    if (initialData.recipe_items?.length > 0) {
      setRecipeItems(initialData.recipe_items);
    }
    
  }, [initialData, initialLoadComplete]);
  
  // Effect to fetch recipe items after initial load
  useEffect(() => {
    if (!initialData || !initialLoadComplete) return;
    
    let isMounted = true;
    
    const fetchItems = async () => {
      try {
        const items = await fetchRecipeItems(initialData.autotreat_mfr_recipe_cloud_id);
        
        if (!isMounted) return;
        
        if (items?.length > 0) {
          setRecipeItems(items);
        }
      } catch (error) {
        console.error('Error fetching recipe items:', error);
      }
    };
    
    fetchItems();
    
    return () => {
      isMounted = false;
    };
  }, [fetchRecipeItems, initialData?.autotreat_mfr_recipe_cloud_id, initialLoadComplete]);

  const fetchMfrLiquids = async () => {
    try {
      const response = await axios.get('/api/admin/autotreat_mfr/mfr_liquids/', {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      });
      setMfrLiquids(response.data);
    } catch (error) {
      console.error('Error fetching MFR liquids:', error);
    }
  };

  const fetchLiquidRateTypes = async () => {
    try {
      const response = await axios.get('/api/admin/autotreat_mfr/liquid_rate_types/', {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      });
      setLiquidRateTypes(response.data);
    } catch (error) {
      console.error('Error fetching liquid rate types:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleRecipeItemChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setNewRecipeItem({
      ...newRecipeItem,
      [name as string]: value,
    });
  };

  const handleRecipeItemSelectChange = (name: string, value: string) => {
    setNewRecipeItem({
      ...newRecipeItem,
      [name]: value,
    });
  };

  const handleAddRecipeItem = () => {
    const selectedLiquid = mfrLiquids.find((l) => l.global_liquid_id === newRecipeItem.mfr_liquid);

    if (newRecipeItem.mfr_liquid && newRecipeItem.liquid_rate && newRecipeItem.liquid_rate_type_id) {
      const newItem = {
        autotreat_mfr_recipe_item_cloud_id: Date.now(),
        global_liquid_id: newRecipeItem.mfr_liquid,
        liquid_rate_type_id: newRecipeItem.liquid_rate_type_id,
        liquid_name: selectedLiquid ? selectedLiquid.liquid_name : '',
        liquid_rate: newRecipeItem.liquid_rate,
        liquid_rate_type:
          liquidRateTypes.find((type) => type.liquid_rate_type_id === newRecipeItem.liquid_rate_type_id)
            ?.liquid_rate_type || '',
        locked: false,
        active: true,
      };

      setNewRecipeItem({ mfr_liquid: '', liquid_name: '', liquid_rate: '', liquid_rate_type_id: '' });
      setRecipeItems([...recipeItems, newItem]);
    } else {
      showSnackbar('Please fill out all fields before adding a recipe item.', 'error');
    }
  };

  const handleRemoveRecipeItem = (itemId: number) => {
    setRecipeItems((prevItems) => prevItems.filter((item) => item.autotreat_mfr_recipe_item_cloud_id !== itemId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Combine form data and recipe items into a single payload
      const payload = {
        ...formValues,
        recipe_items: recipeItems.map((item) => ({
          global_liquid_id: item.global_liquid_id,
          liquid_rate: item.liquid_rate,
          liquid_rate_type_id: item.liquid_rate_type_id,
          locked: item.locked || false,
          active: item.active !== false,
          obsolete: false,
        })),
      };

      // If we have a crop object, extract the crop_id
      if (payload.crop && typeof payload.crop === 'object') {
        payload.crop_id = payload.crop.crop_id;
        delete payload.crop;
      }

      const url = id 
        ? `/api/admin/autotreat_mfr/mfr_recipes/${id}/`
        : '/api/admin/autotreat_mfr/mfr_recipes/';
      
      const method = id ? 'put' : 'post';
      
      await axios[method](url, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('accessToken')}`,
        },
      });

      onSuccess();
    } catch (error: any) {
      if (error?.response?.status === 400) {
        const errorData = error.response.data;
        let errorMessage = 'Failed to save recipe. Please check the form for errors.';
        
        if (typeof errorData === 'object' && errorData !== null) {
          const messages = Object.entries(errorData).map(([field, errors]) => {
            if (Array.isArray(errors)) {
              return `${field}: ${errors.join(', ')}`;
            }
            return `${field}: ${String(errors)}`;
          });
          errorMessage = messages.join('\n');
        } else {
          errorMessage = JSON.stringify(errorData);
        }
        
        showSnackbar(errorMessage, 'error');
        setErrorMessages(errorData);
      } else {
        const errorMessage = error?.response?.data?.message || 'Failed to save recipe. Please try again.';
        showSnackbar(errorMessage, 'error');
        console.error('Error saving recipe:', error);
      }
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Debug log to check recipe items
  console.log('Recipe Items in EditMfrRecipeForm:', recipeItems);

  return (
    <form id="dialog-form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Row 1 */}
        <Grid container item spacing={3}>
          <Grid item xs={12} sm={6}>
            <Select
              fullWidth
              value={formValues.global_mfr_id || ''}
              onChange={(e) => handleSelectChange('global_mfr_id', e.target.value as string)}
              displayEmpty
              variant="outlined"
              required
              renderValue={(selected) => {
                if (!selected) return <em>Select Manufacturer</em>;
                const selectedMfr = globalMfrs.find((mfr: any) => mfr.global_mfr_id === selected);
                return selectedMfr?.mfr_name || selected;
              }}
            >
              <MenuItem value="" disabled>
                <em>Select Manufacturer</em>
              </MenuItem>
              {globalMfrs.map((mfr: any) => (
                <MenuItem key={mfr.global_mfr_id} value={mfr.global_mfr_id}>
                  {mfr.mfr_name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              label="Recipe Name"
              name="liquid_recipe_name"
              value={formValues.liquid_recipe_name || ''}
              onChange={handleInputChange}
              variant="outlined"
            />
          </Grid>
        </Grid>

        {/* Row 2 */}
        <Grid container item spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Description"
              name="liquid_recipe_desc"
              value={formValues.liquid_recipe_desc || ''}
              onChange={handleInputChange}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Select
              fullWidth
              value={formValues.crop_id || ''}
              onChange={(e) => {
                const selectedCropId = e.target.value as string;
                const selectedCrop = crops?.find(c => 
                  String(c.crop_id) === String(selectedCropId) || 
                  String(c.id) === String(selectedCropId)
                );
                
                setFormValues(prev => ({
                  ...prev,
                  crop: selectedCrop || null,
                  crop_id: selectedCropId
                }));
              }}
              displayEmpty
              variant="outlined"
              required
              renderValue={(selected) => {
                if (formValues.crop) {
                  const crop = formValues.crop as Crop;
                  return crop.name || crop.crop_name || `Crop ${crop.crop_id || crop.id || ''}`;
                }
                
                if (selected) {
                  const selectedCrop = crops?.find(c => 
                    String(c.crop_id) === String(selected) || 
                    String(c.id) === String(selected)
                  );
                  
                  if (selectedCrop) {
                    return selectedCrop.name || selectedCrop.crop_name || `Crop ${selectedCrop.crop_id || selectedCrop.id}`;
                  }
                }
                
                return <em>Select Crop</em>;
              }}
            >
              <MenuItem value="" disabled>
                <em>Select Crop</em>
              </MenuItem>
              {crops && crops.length > 0 ? (
                crops.map((crop: any) => {
                  const cropId = crop.crop_id || crop.id;
                  const cropName = crop.name || crop.crop_name || `Crop ${cropId}`;
                  
                  return (
                    <MenuItem key={cropId} value={cropId}>
                      {cropName}
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem disabled>No crops available</MenuItem>
              )}
            </Select>
          </Grid>
        </Grid>

        {/* Row 3 */}
        <Grid container item spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Max Volume"
              name="recipe_volume_max"
              type="number"
              value={formValues.recipe_volume_max || ''}
              onChange={handleInputChange}
              variant="outlined"
              inputProps={{
                min: 0,
                step: 'any'
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Recommended Volume"
              name="recipe_volume_recommended"
              type="number"
              value={formValues.recipe_volume_recommended || ''}
              onChange={handleInputChange}
              variant="outlined"
              inputProps={{
                min: 0,
                step: 'any'
              }}
            />
          </Grid>
        </Grid>

        {/* Row 4 */}
        <Grid container item spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Seed Count/Scale UOW"
              name="seed_cnt_per_scale_uow"
              type="number"
              value={formValues.seed_cnt_per_scale_uow || ''}
              onChange={handleInputChange}
              variant="outlined"
              inputProps={{
                min: 0,
                step: 'any'
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Unit Size Count"
              name="unit_size_cnt"
              type="number"
              value={formValues.unit_size_cnt || ''}
              onChange={handleInputChange}
              variant="outlined"
              inputProps={{
                min: 0,
                step: 'any'
              }}
            />
          </Grid>
        </Grid>

        {/* Checkboxes Row */}
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  name="locked"
                  checked={formValues.locked || false}
                  onChange={handleInputChange}
                />
              }
              label="Locked"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  name="active"
                  checked={formValues.active !== false}
                  onChange={handleInputChange}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>

        {/* Recipe Items Section */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Recipe Items
          </Typography>
        
        {/* Add Recipe Item Form */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Select
              fullWidth
              value={newRecipeItem.mfr_liquid}
              onChange={(e) => handleRecipeItemSelectChange('mfr_liquid', e.target.value as string)}
              displayEmpty
              variant="outlined"
            >
              <MenuItem value="" disabled>
                Select Liquid
              </MenuItem>
              {mfrLiquids.map((liquid) => (
                <MenuItem key={liquid.global_liquid_id} value={liquid.global_liquid_id}>
                  {liquid.liquid_name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Rate"
              name="liquid_rate"
              type="number"
              value={newRecipeItem.liquid_rate}
              onChange={handleRecipeItemChange}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Select
              fullWidth
              value={newRecipeItem.liquid_rate_type_id}
              onChange={(e) => handleRecipeItemSelectChange('liquid_rate_type_id', e.target.value as string)}
              displayEmpty
              variant="outlined"
            >
              <MenuItem value="" disabled>
                Select Rate Type
              </MenuItem>
              {liquidRateTypes.map((type) => (
                <MenuItem key={type.liquid_rate_type_id} value={type.liquid_rate_type_id}>
                  {type.liquid_rate_type}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} sm={1}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleAddRecipeItem}
            >
              Add
            </Button>
          </Grid>
        </Grid>

        {/* Recipe Items List */}
        <Box mt={2}>
          {recipeItems.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-label="recipe items table">
                <TableHead>
                  <TableRow>
                    <TableCell>Liquid Name</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell>Rate Type</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recipeItems.map((item) => (
                    <TableRow 
                      key={item.autotreat_mfr_recipe_item_cloud_id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {item.liquid_name}
                      </TableCell>
                      <TableCell align="right">{item.liquid_rate}</TableCell>
                      <TableCell>{item.liquid_rate_type}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveRecipeItem(item.autotreat_mfr_recipe_item_cloud_id)}
                          color="error"
                          aria-label="delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box 
              sx={{ 
                p: 2, 
                border: '1px dashed', 
                borderColor: 'divider', 
                borderRadius: 1, 
                textAlign: 'center',
                backgroundColor: 'action.hover',
                color: 'text.secondary'
              }}
            >
              <Typography variant="body2">
                No recipe items added yet. Add items using the form above.
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
      </form>
    );
  };

export default EditMfrRecipeForm;
